import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import {
  RuleEngineApi,
  CreateUpdateRuleRequest,
} from "@/api/giftcard.api";

const ruleFormSchema = z.object({
  ruleTitle: z.string().min(1, "Rule title is required"),
  ruleDescription: z.string().optional(),
  // Earn Rule fields
  pointsIssuanceType: z.string().optional(),
  minInvoiceAmount: z.string().optional(),
  awardPoints: z.string().optional(),
  // Validity Rule fields
  pointsValidityType: z.string().optional(),
  pointsValidityDays: z.string().optional(),
  // Redemption Rule fields
  minInvoiceAmountRedemption: z.string().optional(),
  maxRedemptionPoints: z.string().optional(),
  redemptionCriteria: z.string().optional(),
  earnWhileRedeem: z.string().optional(),
});

type RuleFormValues = z.infer<typeof ruleFormSchema>;

interface CreateRuleFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  editMode?: boolean;
  initialValues?: {
    ruleId?: number;
    title?: string;
    description?: string;
    earnRule?: {
      pointsIssuanceType?: string;
      minInvoiceAmount?: number;
      awardPoints?: number;
    };
    validityRule?: {
      pointsValidityType?: string;
      pointsValidityDays?: number;
    };
    redemptionRule?: {
      minInvoiceAmount?: number;
      maxRedemptionPoints?: number;
      redemptionCriteria?: string;
      earnWhileRedeem?: string;
    };
  };
}

export function CreateRuleForm({
  onSuccess,
  onCancel,
  editMode = false,
  initialValues,
}: CreateRuleFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("earn");

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: {
      ruleTitle: initialValues?.title || "",
      ruleDescription: initialValues?.description || "",
      // Earn Rule defaults
      pointsIssuanceType: initialValues?.earnRule?.pointsIssuanceType || "%OfInvoiceAmount",
      minInvoiceAmount: initialValues?.earnRule?.minInvoiceAmount?.toString() || "100",
      awardPoints: initialValues?.earnRule?.awardPoints?.toString() || "10",
      // Validity Rule defaults
      pointsValidityType: initialValues?.validityRule?.pointsValidityType || "Fixed",
      pointsValidityDays: initialValues?.validityRule?.pointsValidityDays?.toString() || "30",
      // Redemption Rule defaults
      minInvoiceAmountRedemption: initialValues?.redemptionRule?.minInvoiceAmount?.toString() || "50",
      maxRedemptionPoints: initialValues?.redemptionRule?.maxRedemptionPoints?.toString() || "1000",
      redemptionCriteria: initialValues?.redemptionRule?.redemptionCriteria || "%OfInvoiceAmount",
      earnWhileRedeem: initialValues?.redemptionRule?.earnWhileRedeem || "PartialEarn",
    },
  });

  const onSubmit = async (data: RuleFormValues) => {
    setIsSubmitting(true);
    try {
      // Determine which rules are enabled based on filled fields
      const isEarnRule =
        data.pointsIssuanceType &&
        data.pointsIssuanceType.trim() !== "" &&
        data.minInvoiceAmount &&
        data.minInvoiceAmount.trim() !== "" &&
        data.awardPoints &&
        data.awardPoints.trim() !== "";

      const isValidityRule =
        data.pointsValidityType &&
        data.pointsValidityType.trim() !== "" &&
        data.pointsValidityDays &&
        data.pointsValidityDays.trim() !== "";

      const isRedemptionRule =
        (data.redemptionCriteria && data.redemptionCriteria.trim() !== "") ||
        (data.earnWhileRedeem && data.earnWhileRedeem.trim() !== "") ||
        (data.minInvoiceAmountRedemption &&
          data.minInvoiceAmountRedemption.trim() !== "") ||
        (data.maxRedemptionPoints &&
          data.maxRedemptionPoints.trim() !== "");

      const payload: CreateUpdateRuleRequest = {
        title: data.ruleTitle,
        isEarnRule: isEarnRule || false,
        isValidityRule: isValidityRule || false,
        isRedemptionRule: isRedemptionRule || false,
        createdBy: 0,
        updatedBy: 0,
        p_id: editMode && initialValues?.ruleId ? initialValues.ruleId : 0,
        ...(isEarnRule && {
          ...(data.pointsIssuanceType &&
            data.pointsIssuanceType !== "PerCategoryProductCSV" && {
              pointsIssuanceType: data.pointsIssuanceType,
            }),
          ...(data.minInvoiceAmount && {
            minInvoiceAmt: Number(data.minInvoiceAmount),
          }),
          ...(data.awardPoints && {
            awardPoints: Number(data.awardPoints),
          }),
        }),
        ...(isValidityRule && {
          ...(data.pointsValidityType && {
            pointsValidityType: data.pointsValidityType,
          }),
          ...(data.pointsValidityDays && {
            pointsValidityDays: Number(data.pointsValidityDays),
          }),
        }),
        ...(isRedemptionRule && {
          ...(data.minInvoiceAmountRedemption &&
            data.minInvoiceAmountRedemption.trim() !== "" && {
              burnMinInvoiceAmt: Number(data.minInvoiceAmountRedemption),
            }),
          ...(data.maxRedemptionPoints &&
            data.maxRedemptionPoints.trim() !== "" && {
              maxRedemptionPoints: Number(data.maxRedemptionPoints),
            }),
          ...(data.redemptionCriteria &&
            data.redemptionCriteria.trim() !== "" && {
              redemptionCriteria: data.redemptionCriteria,
            }),
          ...(data.earnWhileRedeem &&
            data.earnWhileRedeem.trim() !== "" && {
              earnWhileRedeem: data.earnWhileRedeem,
            }),
        }),
      };

      // Log the payload for debugging
      console.log("========== RULE ENGINE API PAYLOAD ==========");
      console.log(JSON.stringify(payload, null, 2));
      console.log("==============================================");

      // Call the API
      const response = await RuleEngineApi.createUpdateRule(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: editMode
            ? "Rule updated successfully"
            : response.message || "Rule created successfully",
        });

        form.reset();
        setActiveTab("earn");
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error(
        editMode ? "Error updating rule:" : "Error creating rule:",
        error
      );
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description:
          errorMessage ||
          (editMode ? "Failed to update rule" : "Failed to create rule"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (editMode && initialValues) {
      // Reset to initial values in edit mode
      form.reset({
        ruleTitle: initialValues.title || "",
        ruleDescription: initialValues.description || "",
        pointsIssuanceType: initialValues.earnRule?.pointsIssuanceType || "%OfInvoiceAmount",
        minInvoiceAmount: initialValues.earnRule?.minInvoiceAmount?.toString() || "100",
        awardPoints: initialValues.earnRule?.awardPoints?.toString() || "10",
        pointsValidityType: initialValues.validityRule?.pointsValidityType || "Fixed",
        pointsValidityDays: initialValues.validityRule?.pointsValidityDays?.toString() || "30",
        minInvoiceAmountRedemption: initialValues.redemptionRule?.minInvoiceAmount?.toString() || "50",
        maxRedemptionPoints: initialValues.redemptionRule?.maxRedemptionPoints?.toString() || "1000",
        redemptionCriteria: initialValues.redemptionRule?.redemptionCriteria || "%OfInvoiceAmount",
        earnWhileRedeem: initialValues.redemptionRule?.earnWhileRedeem || "PartialEarn",
      });
    } else {
      form.reset();
    }
    setActiveTab("earn");
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Common Fields */}
          <FormField
            control={form.control}
            name="ruleTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rule title *</FormLabel>
                <FormControl>
                  <Input placeholder="Enter rule title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ruleDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rule description (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Enter rule description (optional)"
                    {...field}
                    rows={3}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Tabs for Earn, Validity, Redemption */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="earn">Earn rule</TabsTrigger>
              <TabsTrigger value="validity">Validity rule</TabsTrigger>
              <TabsTrigger value="redemption">Redemption rule</TabsTrigger>
            </TabsList>

            {/* Earn Rule Tab */}
            <TabsContent value="earn" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Earn rule</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Define how points are issued.
                </p>
              </div>

              <FormField
                control={form.control}
                name="pointsIssuanceType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Points Issuance Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select points issuance type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="%OfInvoiceAmount">
                          % of Invoice Amount
                        </SelectItem>
                        <SelectItem value="FixedPointsPerTxn">
                          Fixed point per transaction
                        </SelectItem>
                        <SelectItem value="PerCategoryProductCSV">
                          Per Category / Product (Upload CSV)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="minInvoiceAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min invoice amount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum invoice amount"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="awardPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Award points</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter award points"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>

            {/* Validity Rule Tab */}
            <TabsContent value="validity" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Validity rule</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Set the validity period for earned points.
                </p>
              </div>

              <FormField
                control={form.control}
                name="pointsValidityType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Points Validity Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select points validity type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Fixed">Fixed</SelectItem>
                        <SelectItem value="Revolving">Revolving</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pointsValidityDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Points Validity(days)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter validity days"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>

            {/* Redemption Rule Tab */}
            <TabsContent value="redemption" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Redemption rule</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure how customers can redeem their earned points.
                </p>
              </div>

              <FormField
                control={form.control}
                name="minInvoiceAmountRedemption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Min Invoice Amount (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum invoice amount"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maxRedemptionPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Max points redeemable in single bill (Optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter max redemption points"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="redemptionCriteria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Redemption Criteria</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select redemption criteria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="%OfInvoiceAmount">
                          % of Invoice Amount
                        </SelectItem>
                        <SelectItem value="%OfAvailablePoints">
                          % Available Points
                        </SelectItem>
                        <SelectItem value="NoCriteria">No Criteria</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="earnWhileRedeem"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Earn while Redeem</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select earn while redeem option" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="PartialEarn">Partial Earn</SelectItem>
                        <SelectItem value="FullEarn">Full Earn</SelectItem>
                        <SelectItem value="NoEarn">No Earn</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TabsContent>
          </Tabs>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
